import { useEffect, useRef, RefObject } from "react";

const FOCUSABLE_SELECTOR =
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

interface UseFocusTrapOptions {
    isActive: boolean;
    onEscape: () => void;
    initialFocusRef?: RefObject<HTMLElement | null>;
    fallbackFocusRef?: RefObject<HTMLElement | null>;
}

export const useFocusTrap = (
    containerRef: RefObject<HTMLElement | null>,
    { isActive, onEscape, initialFocusRef, fallbackFocusRef }: UseFocusTrapOptions,
) => {
    const previousFocusRef = useRef<HTMLElement | null>(null);
    // the keydown listener reads the callback through a ref, so a caller that recreates
    // onEscape on every render does not re-run the effects below
    const onEscapeRef = useRef(onEscape);

    useEffect(() => {
        onEscapeRef.current = onEscape;
    }, [onEscape]);

    // Focus handling runs only when the trap is activated/deactivated. It must never re-run
    // on an unrelated re-render: moving focus into the dialog blurs whatever was focused
    // before, and a blur handler that updates state (an accounting grid cell, for instance)
    // would then re-render us and trigger the focus move again - an endless loop that React
    // aborts with "Maximum update depth exceeded" (minified error #185).
    useEffect(() => {
        if (!isActive) return;

        previousFocusRef.current = document.activeElement as HTMLElement;

        if (initialFocusRef?.current) {
            initialFocusRef.current.focus();
        } else {
            const container = containerRef.current;
            const alreadyFocusedInside = container?.contains(document.activeElement) ?? false;
            if (!alreadyFocusedInside) {
                fallbackFocusRef?.current?.focus();
            }
        }

        return () => {
            previousFocusRef.current?.focus();
            previousFocusRef.current = null;
        };
        // containerRef/initialFocusRef/fallbackFocusRef are refs and stable by contract
    }, [isActive]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!isActive) return;

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                onEscapeRef.current();
                return;
            }

            if (event.key === "Tab" && containerRef.current) {
                const focusableElements =
                    containerRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
                const firstFocusable = focusableElements[0];
                const lastFocusable = focusableElements[focusableElements.length - 1];

                if (event.shiftKey && document.activeElement === firstFocusable) {
                    event.preventDefault();
                    lastFocusable?.focus();
                } else if (!event.shiftKey && document.activeElement === lastFocusable) {
                    event.preventDefault();
                    firstFocusable?.focus();
                }
            }
        };

        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [isActive, containerRef]);
};
